/** Viewport-edge docking geometry; independent of pointer or storage APIs. */
export type RibbonEdge = 'left' | 'right' | 'top' | 'bottom';
export interface RibbonDock {
  edge: RibbonEdge;
  /** Distance from the top (vertical edges) or left (horizontal edges). */
  offset: number;
}
export interface Viewport {
  width: number;
  height: number;
}
export interface Point {
  x: number;
  y: number;
}

const EDGES: RibbonEdge[] = ['left', 'right', 'top', 'bottom'];
const clamp = (value: number, low: number, high: number): number =>
  Math.max(low, Math.min(value, high));
const dimension = (value: number): number => (Number.isFinite(value) ? Math.max(0, value) : 0);

/**
 * Only the outer 50px (at most one quarter of the viewport) attracts a released ribbon. A central
 * drop returns null so the caller can restore its last committed dock.
 * Corner ties follow the gesture, then the previous edge, then a stable order.
 */
export function chooseRibbonEdge(
  point: Point | null,
  viewport: Viewport,
  previousEdge: RibbonEdge,
  movement: Point = { x: 0, y: 0 },
  excludeEdge?: RibbonEdge,
): RibbonEdge | null {
  if (
    !point ||
    !Number.isFinite(point.x) ||
    !Number.isFinite(point.y) ||
    !Number.isFinite(viewport.width) ||
    !Number.isFinite(viewport.height) ||
    viewport.width <= 0 ||
    viewport.height <= 0
  )
    return null;

  const x = clamp(point.x, 0, viewport.width);
  const y = clamp(point.y, 0, viewport.height);
  const distances: Record<RibbonEdge, number> = {
    left: x,
    right: viewport.width - x,
    top: y,
    bottom: viewport.height - y,
  };
  const limits: Record<RibbonEdge, number> = {
    left: Math.min(viewport.width / 4, 50),
    right: Math.min(viewport.width / 4, 50),
    top: Math.min(viewport.height / 4, 50),
    bottom: Math.min(viewport.height / 4, 50),
  };
  const dx = Number.isFinite(movement.x) ? movement.x : 0;
  const dy = Number.isFinite(movement.y) ? movement.y : 0;
  const direction: Record<RibbonEdge, number> = { left: -dx, right: dx, top: -dy, bottom: dy };
  const eligible = EDGES.filter((edge) => edge !== excludeEdge && distances[edge] <= limits[edge]);
  eligible.sort((a, b) => {
    const distance = distances[a] - distances[b];
    if (Math.abs(distance) > 0.000001) return distance;
    const trajectory = direction[b] - direction[a];
    if (trajectory !== 0) return trajectory;
    return Number(b === previousEdge) - Number(a === previousEdge);
  });
  return eligible[0] ?? null;
}

/** Clamp the whole ribbon to the visible viewport, retaining its docked edge. */
export function ribbonPlacement(
  dock: RibbonDock,
  viewport: Viewport,
  size: { width: number; height: number },
  insets: Partial<Record<RibbonEdge, number>> = {},
): { x: number; y: number; offset: number } {
  const width = dimension(viewport.width);
  const height = dimension(viewport.height);
  const maxX = Math.max(0, width - dimension(size.width));
  const maxY = Math.max(0, height - dimension(size.height));
  const vertical = dock.edge === 'left' || dock.edge === 'right';
  const available = vertical ? maxY : maxX;
  const margin = available >= 16 ? 8 : 0;
  const offset = clamp(Number.isFinite(dock.offset) ? dock.offset : 0, margin, available - margin);
  const inset = dimension(insets[dock.edge] ?? 0);
  switch (dock.edge) {
    case 'left':
      return { x: Math.min(inset, maxX), y: offset, offset };
    case 'right':
      return { x: Math.max(0, maxX - inset), y: offset, offset };
    case 'top':
      return { x: offset, y: Math.min(inset, maxY), offset };
    case 'bottom':
      return { x: offset, y: Math.max(0, maxY - inset), offset };
  }
}

/** Malformed or older storage never controls CSS or leaves the ribbon offscreen. */
export function parseRibbonDock(serialized: string | null): RibbonDock | null {
  if (!serialized || serialized.length > 256) return null;
  try {
    const value: unknown = JSON.parse(serialized);
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const candidate = value as Record<string, unknown>;
    if (
      Object.keys(candidate).length !== 2 ||
      !EDGES.includes(candidate.edge as RibbonEdge) ||
      typeof candidate.offset !== 'number' ||
      !Number.isFinite(candidate.offset) ||
      candidate.offset < 0
    )
      return null;
    return { edge: candidate.edge as RibbonEdge, offset: candidate.offset };
  } catch {
    return null;
  }
}
